import { useState } from 'react';
import { UnitOfMeasure, UnitOfMeasureCreate, UnitOfMeasureUpdate } from '../types/costwand.d';
import { useUnitsOfMeasure } from '../hooks/useUnitsOfMeasure.ts';
import { useCurrencyLookup } from '../hooks/useCurrencyLookup.ts';
import UnitOfMeasureForm from '../components/UnitOfMeasureForm.tsx';
import UnitOfMeasureTable from '../components/UnitOfMeasureTable.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '../components/ui/dialog.tsx';

const UnitsOfMeasurePage = () => {
  const {
    unitsOfMeasure,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    handlePageChange,
    createUnitOfMeasure,
    updateUnitOfMeasure,
    deleteUnitOfMeasure,
    fetchUnitsOfMeasure,
  } = useUnitsOfMeasure();

  const { currencies, loading: currenciesLoading, error: currenciesError } = useCurrencyLookup();

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [editingUnitOfMeasure, setEditingUnitOfMeasure] = useState<UnitOfMeasure | null>(null);
  const [formSubmitting, setFormSubmitting] = useState<boolean>(false);

  const handleAddUnitOfMeasure = () => {
    setEditingUnitOfMeasure(null);
    setIsFormOpen(true);
  };

  const handleEditUnitOfMeasure = (unitOfMeasure: UnitOfMeasure) => {
    setEditingUnitOfMeasure(unitOfMeasure);
    setIsFormOpen(true);
  };

  const handleDeleteUnitOfMeasure = async (id: number) => {
    await deleteUnitOfMeasure(id);
    fetchUnitsOfMeasure(currentPage);
  };

  const handleFormSubmit = async (data: UnitOfMeasureCreate | UnitOfMeasureUpdate) => {
    setFormSubmitting(true);

    try {
      let success = false;
      if (editingUnitOfMeasure) {
        success = await updateUnitOfMeasure(editingUnitOfMeasure.id!, data as UnitOfMeasureUpdate);
      } else {
        success = await createUnitOfMeasure(data as UnitOfMeasureCreate);
      }

      if (success) {
        setIsFormOpen(false);
        fetchUnitsOfMeasure(currentPage);
      }
    } catch (e: any) {
      // Error handling is managed by the global error context
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingUnitOfMeasure(null);
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold text-secondary-foreground">Unit of Measure Management</h2>
        <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
          <DialogTrigger asChild>
            <Button onClick={handleAddUnitOfMeasure} disabled={currenciesLoading}>
              Add New Unit of Measure
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>{editingUnitOfMeasure ? 'Edit Unit of Measure' : 'Add New Unit of Measure'}</DialogTitle>
              <DialogDescription>
                {editingUnitOfMeasure ? 'Make changes to the unit of measure here.' : 'Add a new unit of measure to your list.'}
              </DialogDescription>
            </DialogHeader>
            {currenciesError && (
              <p className="text-destructive text-sm p-2 bg-red-100 border border-red-400 rounded">
                Error loading currencies: {currenciesError}
              </p>
            )}
            <UnitOfMeasureForm
              initialData={editingUnitOfMeasure}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
              isSubmitting={formSubmitting}
              currencies={currencies}
              lookupLoading={currenciesLoading}
            />
          </DialogContent>
        </Dialog>
      </div>

      <UnitOfMeasureTable
        unitsOfMeasure={unitsOfMeasure}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        onEdit={handleEditUnitOfMeasure}
        onDelete={handleDeleteUnitOfMeasure}
        loading={loading}
        error={error}
        baseEndpoint="/units_of_measure" // NEW: Pass the base endpoint
      />
    </div>
  );
};

export default UnitsOfMeasurePage;